import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeviceCalls } from "@/features/devices/hooks/use-devices";
import type { Device, DeviceCall } from "@/features/devices/types";
import { useClientId } from "@/features/utils/api-scope";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/** "4 min 12 s", or a dash for a call that was never connected. */
const duration = (seconds: number) => {
  if (!seconds) return "—";
  const minutes = Math.floor(seconds / 60);
  return minutes ? `${minutes} min ${seconds % 60}s` : `${seconds}s`;
};

const Outcome = ({ call }: { call: DeviceCall }) => {
  const { t } = useTranslation("devices");

  if (call.status === "refused")
    return (
      <Badge variant="destructive">
        {/*
          The reason, translated when we know it and passed through when we do
          not. A refusal code we have no wording for is still worth showing —
          `blocked_prefix` in English beats an empty cell that reads as a bug.
        */}
        {t(`devices.refusals.${call.refusal_reason}`, {
          defaultValue: call.refusal_reason,
        })}
      </Badge>
    );

  if (call.status === "authorized")
    return <Badge variant="outline">{t("devices.calls.inProgress")}</Badge>;

  // ANSWER is the only outcome worth no comment; everything else is why the
  // person on this phone did not get through.
  if (call.dial_status && call.dial_status !== "ANSWER")
    return <Badge variant="secondary">{call.dial_status}</Badge>;

  return <Badge variant="secondary">{t("devices.calls.answered")}</Badge>;
};

/**
 * What a handset has been dialling.
 *
 * Refusals share the list with connected calls rather than being filtered out
 * or tucked behind a toggle: a stolen handset shows up here as a run of
 * refusals against ranges nobody in the office dials, and a list that only
 * shows successful calls is exactly the list where that is invisible.
 *
 * Mounted once at page level (see the page component) rather than in the row
 * that opened it — the list refetches on an interval, and state in a table cell
 * does not survive the row being reconciled.
 */
export const DeviceCallsDialog = ({
  device,
  onClose,
}: {
  device: Device | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation("devices");
  const locale = useDateFnsLocale();
  const clientId = useClientId();

  const { data: calls = [], isLoading } = useDeviceCalls(
    clientId || "",
    device?.id || "",
    !!device,
  );

  return (
    <Dialog open={!!device} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("devices.calls.title")}</DialogTitle>
          <DialogDescription>
            {t("devices.calls.description", { label: device?.label || "" })}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="text-muted-foreground text-sm">
            {t("devices.calls.loading")}
          </p>
        )}

        {!isLoading && !calls.length && (
          <p className="text-muted-foreground text-sm">
            {t("devices.calls.empty")}
          </p>
        )}

        {!!calls.length && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("devices.calls.when")}
                  </TableHead>
                  <TableHead>
                    {t("devices.calls.destination")}
                  </TableHead>
                  <TableHead>
                    {t("devices.calls.duration")}
                  </TableHead>
                  <TableHead>
                    {t("devices.calls.outcome")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calls.map((call) => (
                  <TableRow key={call.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(call.created_at), "Pp", { locale })}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {/* Empty on a refusal we could not even parse. */}
                      {call.destination || "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {duration(call.answered_seconds)}
                    </TableCell>
                    <TableCell>
                      <Outcome call={call} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
